import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import type { ChatChannel } from './useChatChannels';

export interface DmParticipant {
  id: string;
  name: string;
  avatar: string | null;
}

/**
 * For each DM channel, resolves the "other" participant (not the current user).
 * Returns a Map of channelId → DmParticipant.
 */
export function useDmParticipants(channels: ChatChannel[]) {
  const { user } = useUser();
  const [participants, setParticipants] = useState<Map<string, DmParticipant>>(
    new Map()
  );

  useEffect(() => {
    if (!user) return;

    const dmChannels = channels.filter(ch => ch.type === 'direct');
    if (dmChannels.length === 0) {
      setParticipants(new Map());
      return;
    }

    const fetchParticipants = async () => {
      // Get all members of DM channels
      const { data: members, error: memberError } = await supabase
        .from('chat_channel_members')
        .select('channel_id, user_id')
        .in(
          'channel_id',
          dmChannels.map(ch => ch.id)
        );

      if (memberError || !members) return;

      // Find the "other" user for each DM channel
      const otherUserIds = new Set<string>();
      const channelToOtherUser = new Map<string, string>();

      for (const member of members) {
        if (member.user_id !== user.id) {
          otherUserIds.add(member.user_id);
          channelToOtherUser.set(member.channel_id, member.user_id);
        }
      }

      if (otherUserIds.size === 0) {
        setParticipants(new Map());
        return;
      }

      // Fetch user details
      const { data: users, error: userError } = await supabase
        .from('users')
        .select('id, name, avatar')
        .in('id', Array.from(otherUserIds));

      if (userError || !users) return;

      const userMap = new Map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        users.map((u: any) => [
          u.id,
          { id: u.id, name: u.name || 'Unknown', avatar: u.avatar || null },
        ])
      );

      const newParticipants = new Map<string, DmParticipant>();

      for (const [channelId, userId] of channelToOtherUser) {
        const userData = userMap.get(userId);
        if (userData) {
          newParticipants.set(channelId, userData);
        }
      }

      setParticipants(newParticipants);
    };

    fetchParticipants();
  }, [user, channels]);

  return participants;
}
